import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { products as pApi, deals as dApi, dispensaries as dispApi, dashboardApi, walletApi } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Line, LineChart } from "recharts";
import { Plus, Trash2, Package, Tag, BarChart3, DollarSign, Wallet, ScanLine, Building2, TrendingUp } from "lucide-react";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [dispensary, setDispensary] = useState(null);
  const [products, setProducts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState(null);
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      let d = user.dispensary_id ? await dispApi.get(user.dispensary_id).catch(() => null) : null;
      if (!d) {
        const all = await dispApi.list();
        d = all.find((x) => x.owner_user_id === user.id) || all[0];
      }
      if (!d) return;
      setDispensary(d);
      const [pp, dd, st, w] = await Promise.all([
        pApi.list({ dispensary_id: d.id, limit: 100 }),
        dApi.list().then((x) => x.filter((z) => z.dispensary_id === d.id)),
        dashboardApi.get(d.id),
        walletApi.state(d.id),
      ]);
      setProducts(pp); setDeals(dd); setStats(st); setWallet(w);
    })();
  }, [user]);

  if (loading) return <div className="min-h-screen"><NavBar /><div className="text-center py-20">Loading...</div></div>;
  if (!user) return <Navigate to="/auth?mode=login" replace />;
  if (user.role !== "dispensary" && user.role !== "admin") {
    return <div className="min-h-screen"><NavBar /><div className="max-w-2xl mx-auto py-32 text-center"><h2 className="font-display text-4xl font-extrabold mb-3">Shop owners only</h2><p className="text-[#0B0F0C]/60">Sign up as a dispensary to access this.</p></div></div>;
  }

  const funnel = stats?.funnel || {};
  const stepWidth = (n) => `${Math.min(100, Math.max(15, (n / Math.max(funnel.views || 1, 1)) * 100))}%`;
  const lowBalance = wallet && wallet.balance < 50;

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="blob" style={{ width: 380, height: 380, background: "#15FF73", top: -100, right: -80 }} />
      <NavBar />
      <Toaster position="top-right" theme="light" richColors />
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10 relative">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <span className="sticker sticker-magenta mb-3 inline-block"><Building2 size={11} strokeWidth={2.4} /> Dashboard</span>
            <h1 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight leading-[0.92] mt-3">{dispensary?.name || "Loading..."}</h1>
            <div className="text-sm text-[#0B0F0C]/60 mt-1 font-mono-x">{dispensary?.city}, {dispensary?.state} · {dispensary?.type}</div>
          </div>
          <div className="flex gap-2">
            <Link to="/scan" className="btn-x btn-magenta !py-2 !px-4 !text-xs"><ScanLine size={12} strokeWidth={2.4} /> Scanner</Link>
            <Link to="/wallet" className="btn-x btn-primary !py-2 !px-4 !text-xs"><Wallet size={12} strokeWidth={2.4} /> Wallet</Link>
          </div>
        </div>

        {lowBalance && (
          <div className="card-x card-magenta bg-[#FF1A8C] text-white p-5 mb-6 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3"><span className="text-3xl">⚠️</span><div><div className="font-display text-xl font-extrabold">Wallet low — deals will pause at $0</div><div className="text-sm opacity-90">Top up to keep your traffic flowing.</div></div></div>
            <Link to="/wallet" className="btn-x btn-light">Top up →</Link>
          </div>
        )}

        {/* KPIs */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Kpi label="Products" value={stats?.products ?? 0} icon="📦" tone="card-green" />
          <Kpi label="Active Deals" value={stats?.deals ?? 0} icon="🏷️" tone="card-magenta" />
          <Kpi label="Redemptions" value={stats?.redemptions ?? 0} icon="🎟️" tone="card-cyan" />
          <Kpi label="Wallet" value={`$${(wallet?.balance ?? 0).toFixed(0)}`} icon="💸" tone="card-sunset" highlight />
        </div>

        {/* ROI BAR */}
        <div className="card-x card-purple bg-[#0B0F0C] text-[#F5F0DC] p-6 mb-8 grid md:grid-cols-3 gap-5">
          <div><div className="label-overline text-[#15FF73]">Sales</div><div className="font-display text-4xl font-extrabold mt-1">${(stats?.sales_total ?? 0).toFixed(0)}</div></div>
          <div><div className="label-overline text-[#FF1A8C]">Commission paid</div><div className="font-display text-4xl font-extrabold mt-1">${(stats?.commission_paid ?? 0).toFixed(0)}</div></div>
          <div><div className="label-overline text-[#15FF73]">ROI multiple</div><div className="font-display text-4xl font-extrabold mt-1 text-[#15FF73]">{(stats?.roi_multiple ?? 0).toFixed(1)}×</div></div>
        </div>

        {/* FUNNEL */}
        <div className="card-x card-green bg-[#FAF6E8] p-6 mb-8">
          <div className="label-overline text-[#FF1A8C] mb-4 flex items-center gap-2"><TrendingUp size={12} strokeWidth={2.4} /> Conversion funnel</div>
          <div className="space-y-3">
            <FunnelStep label="Views" value={funnel.views || 0} width={stepWidth(funnel.views || 0)} color="bg-[#15FF73]" />
            <FunnelStep label="Claims" value={funnel.claims || 0} width={stepWidth(funnel.claims || 0)} color="bg-[#FFE74C]" />
            <FunnelStep label="Visits" value={funnel.visits || 0} width={stepWidth(funnel.visits || 0)} color="bg-[#FF7A3D]" />
            <FunnelStep label="Purchases" value={funnel.purchases || 0} width={stepWidth(funnel.purchases || 0)} color="bg-[#FF1A8C]" />
          </div>
        </div>

        {/* CHARTS */}
        <div className="grid lg:grid-cols-2 gap-5 mb-10">
          <ChartCard title="Daily claims · 7d" tone="card-cyan">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={stats?.series ?? []}>
                <CartesianGrid stroke="#0B0F0C20" strokeDasharray="3 3" />
                <XAxis dataKey="day" stroke="#0B0F0C80" fontSize={10} />
                <YAxis stroke="#0B0F0C80" fontSize={10} />
                <Tooltip contentStyle={{ background: "#FAF6E8", border: "2px solid #0B0F0C", borderRadius: 12, fontSize: 12 }} />
                <Line type="monotone" dataKey="claims" stroke="#FF1A8C" strokeWidth={3} dot={{ fill: "#FF1A8C", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Daily redemptions · 7d" tone="card-sunset">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats?.series ?? []}>
                <CartesianGrid stroke="#0B0F0C20" strokeDasharray="3 3" />
                <XAxis dataKey="day" stroke="#0B0F0C80" fontSize={10} />
                <YAxis stroke="#0B0F0C80" fontSize={10} />
                <Tooltip contentStyle={{ background: "#FAF6E8", border: "2px solid #0B0F0C", borderRadius: 12, fontSize: 12 }} />
                <Bar dataKey="redeems" fill="#15FF73" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <Tabs defaultValue="products" className="w-full">
          <TabsList className="bg-transparent p-1 rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8]">
            <TabsTrigger value="products" data-testid="tab-products" className="rounded-full data-[state=active]:bg-[#15FF73] data-[state=active]:shadow-[2px_2px_0_#0B0F0C] font-bold text-xs uppercase tracking-widest px-5">Products</TabsTrigger>
            <TabsTrigger value="deals" data-testid="tab-deals" className="rounded-full data-[state=active]:bg-[#FF1A8C] data-[state=active]:text-white data-[state=active]:shadow-[2px_2px_0_#0B0F0C] font-bold text-xs uppercase tracking-widest px-5">Deals</TabsTrigger>
          </TabsList>
          <TabsContent value="products" className="mt-6">
            <ProductManager dispensary={dispensary} products={products} setProducts={setProducts} />
          </TabsContent>
          <TabsContent value="deals" className="mt-6">
            <DealManager dispensary={dispensary} deals={deals} setDeals={setDeals} />
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}

function Kpi({ icon, label, value, tone, highlight }) {
  return (
    <div className={`card-x ${tone} bg-[#FAF6E8] p-5`}>
      <div className="flex items-center justify-between mb-3"><span className="label-overline text-[#FF1A8C]">{label}</span><span className="text-2xl">{icon}</span></div>
      <div className={`font-display text-4xl font-extrabold ${highlight ? "text-[#FF1A8C]" : ""}`}>{value}</div>
    </div>
  );
}
function ChartCard({ title, children, tone }) {
  return (
    <div className={`card-x ${tone} bg-[#FAF6E8] p-5`}>
      <div className="label-overline text-[#FF1A8C] mb-4 flex items-center gap-2"><BarChart3 size={12} strokeWidth={2.4} /> {title}</div>
      {children}
    </div>
  );
}
function FunnelStep({ label, value, width, color }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-24 font-mono-x text-xs font-bold uppercase tracking-widest">{label}</div>
      <div className="flex-1 h-9 rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8] overflow-hidden">
        <div className={`${color} h-full flex items-center justify-end px-3 text-xs font-bold transition-all duration-700`} style={{ width }}>
          {value}
        </div>
      </div>
    </div>
  );
}

function ProductManager({ dispensary, products, setProducts }) {
  const empty = { name: "", brand: "", category: "flower", strain: "hybrid", thc: 20, cbd: 0, price: 35, weight_g: 3.5, is_top_shelf: false, image_url: "https://images.unsplash.com/photo-1578908226199-40101eab0ac3?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", effects: [], flavors: [] };
  const [form, setForm] = useState(empty);

  const create = async () => {
    if (!dispensary || !form.name) { toast.error("Add a name"); return; }
    try { const p = await pApi.create({ ...form, dispensary_id: dispensary.id }); setProducts([p, ...products]); setForm(empty); toast.success(`Added ${p.name}`); } catch { toast.error("Could not create"); }
  };
  const remove = async (id) => { await pApi.remove(id); setProducts(products.filter((p) => p.id !== id)); toast.success("Removed"); };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 card-x card-cyan bg-[#FAF6E8] p-5">
        <div className="label-overline text-[#FF1A8C] mb-4">Catalog · {products.length}</div>
        <div className="space-y-2 max-h-[520px] overflow-auto pr-2">
          {products.length === 0 && <div className="text-[#0B0F0C]/55 text-sm">No products yet.</div>}
          {products.map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8] p-2.5 hover:bg-[#FFE74C]/20">
              <img src={p.image_url} alt="" className="w-12 h-12 object-cover rounded-xl border-2 border-[#0B0F0C]" />
              <div className="flex-1">
                <div className="font-display font-bold">{p.name}</div>
                <div className="text-[10px] text-[#0B0F0C]/55 font-mono-x font-bold uppercase tracking-widest">{p.strain} · {p.category} · {p.thc}% THC</div>
              </div>
              <div className="font-display font-extrabold">${p.price.toFixed(0)}</div>
              <button data-testid={`del-prod-${p.id}`} onClick={() => remove(p.id)} className="text-[#0B0F0C]/55 hover:text-[#FF1A8C] p-2"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="card-x card-green bg-[#FAF6E8] p-5">
        <div className="label-overline text-[#FF1A8C] mb-4">+ Add product</div>
        <div className="space-y-3">
          <FieldText placeholder="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="prod-name" />
          <FieldText placeholder="Brand" v={form.brand} on={(v) => setForm({ ...form, brand: v })} testid="prod-brand" />
          <div className="grid grid-cols-2 gap-2">
            <FieldSelect value={form.category} on={(v) => setForm({ ...form, category: v })} options={["flower","vape","edible","concentrate","tincture","preroll"]} testid="prod-category" />
            <FieldSelect value={form.strain} on={(v) => setForm({ ...form, strain: v })} options={["indica","sativa","hybrid","cbd"]} testid="prod-strain" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FieldNum placeholder="THC%" v={form.thc} on={(v) => setForm({ ...form, thc: v })} testid="prod-thc" />
            <FieldNum placeholder="CBD%" v={form.cbd} on={(v) => setForm({ ...form, cbd: v })} testid="prod-cbd" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FieldNum placeholder="Price ($)" v={form.price} on={(v) => setForm({ ...form, price: v })} testid="prod-price" />
            <FieldNum placeholder="Weight (g)" v={form.weight_g} on={(v) => setForm({ ...form, weight_g: v })} testid="prod-weight" />
          </div>
          <label className="flex items-center gap-2 text-xs font-bold"><input type="checkbox" checked={form.is_top_shelf} onChange={(e) => setForm({ ...form, is_top_shelf: e.target.checked })} className="accent-[#15FF73] w-4 h-4" /> Top Shelf</label>
          <button data-testid="prod-create" onClick={create} className="btn-x btn-primary w-full !text-sm"><Plus size={14} /> Add</button>
        </div>
      </div>
    </div>
  );
}

function DealManager({ dispensary, deals, setDeals }) {
  const empty = { title: "", description: "", discount_pct: 25, original_price: 50, deal_price: 35, code: "", expires_at: new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 16), is_flash: false };
  const [form, setForm] = useState(empty);
  const create = async () => {
    if (!dispensary || !form.title) { toast.error("Add a title"); return; }
    try { const d = await dApi.create({ ...form, dispensary_id: dispensary.id, expires_at: new Date(form.expires_at).toISOString() }); setDeals([d, ...deals]); setForm(empty); toast.success("Deal created"); } catch { toast.error("Could not create"); }
  };
  const remove = async (id) => { await dApi.remove(id); setDeals(deals.filter((d) => d.id !== id)); toast.success("Removed"); };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 card-x card-magenta bg-[#FAF6E8] p-5">
        <div className="label-overline text-[#FF1A8C] mb-4">Deals · {deals.length}</div>
        <div className="space-y-2 max-h-[520px] overflow-auto pr-2">
          {deals.length === 0 && <div className="text-[#0B0F0C]/55 text-sm">None yet.</div>}
          {deals.map((d) => (
            <div key={d.id} className="flex items-center gap-3 rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8] p-3 hover:bg-[#FFE74C]/20">
              <div className="flex-1">
                <div className="font-display font-bold">{d.title}</div>
                <div className="text-[10px] text-[#0B0F0C]/55 font-mono-x font-bold uppercase">-{d.discount_pct}% · ends {new Date(d.expires_at).toLocaleString()}</div>
              </div>
              <div className="font-display font-extrabold">${d.deal_price.toFixed(0)}</div>
              <button data-testid={`del-deal-${d.id}`} onClick={() => remove(d.id)} className="text-[#0B0F0C]/55 hover:text-[#FF1A8C] p-2"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="card-x card-sunset bg-[#FAF6E8] p-5">
        <div className="label-overline text-[#FF1A8C] mb-4">+ Create deal</div>
        <div className="space-y-3">
          <FieldText placeholder="Title" v={form.title} on={(v) => setForm({ ...form, title: v })} testid="deal-title" />
          <FieldText placeholder="Description" v={form.description} on={(v) => setForm({ ...form, description: v })} testid="deal-desc" />
          <FieldText placeholder="Code" v={form.code} on={(v) => setForm({ ...form, code: v })} testid="deal-code" />
          <div className="grid grid-cols-2 gap-2">
            <FieldNum placeholder="% off" v={form.discount_pct} on={(v) => setForm({ ...form, discount_pct: v })} testid="deal-pct" />
            <FieldNum placeholder="Was $" v={form.original_price} on={(v) => setForm({ ...form, original_price: v })} testid="deal-was" />
          </div>
          <FieldNum placeholder="Now $" v={form.deal_price} on={(v) => setForm({ ...form, deal_price: v })} testid="deal-now" />
          <label className="flex items-center gap-3 rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8] px-4 h-12">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-60">Expires</span>
            <input type="datetime-local" data-testid="deal-expires" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="bg-transparent flex-1 outline-none text-sm font-mono-x" />
          </label>
          <label className="flex items-center gap-2 text-xs font-bold"><input type="checkbox" checked={form.is_flash} onChange={(e) => setForm({ ...form, is_flash: e.target.checked })} className="accent-[#FF1A8C] w-4 h-4" /> Flash deal</label>
          <button data-testid="deal-create" onClick={create} className="btn-x btn-magenta w-full !text-sm"><Plus size={14} /> Create</button>
        </div>
      </div>
    </div>
  );
}

function FieldText({ placeholder, v, on, testid }) {
  return <input data-testid={testid} value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder} className="w-full bg-[#FAF6E8] border-2 border-[#0B0F0C] rounded-full focus:bg-white focus:shadow-[3px_3px_0_#0B0F0C] px-4 h-11 text-sm placeholder:text-[#0B0F0C]/40 outline-none transition-all" />;
}
function FieldNum({ placeholder, v, on, testid }) {
  return <input data-testid={testid} type="number" step="0.1" value={v} onChange={(e) => on(parseFloat(e.target.value) || 0)} placeholder={placeholder} className="w-full bg-[#FAF6E8] border-2 border-[#0B0F0C] rounded-full focus:bg-white focus:shadow-[3px_3px_0_#0B0F0C] px-4 h-11 text-sm placeholder:text-[#0B0F0C]/40 font-mono-x font-bold outline-none transition-all" />;
}
function FieldSelect({ value, on, options, testid }) {
  return (
    <Select value={value} onValueChange={on}>
      <SelectTrigger data-testid={testid} className="rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8] h-11 font-bold"><SelectValue /></SelectTrigger>
      <SelectContent className="rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8]">
        {options.map((o) => <SelectItem key={o} value={o} className="rounded-lg">{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
